import { localDatabaseUrls } from "../backend/constants";
import { getJWTToken } from "../shared/fetchWrapper/fetchWrapper";
import { getAPI, putAPI } from "../backend/commons";
import AlertManager from "../shared/Alert/Alert";

const pickFirstDefined = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return value;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : value;
};

const setIfDefined = (target, key, value) => {
  if (value !== undefined) {
    target[key] = value;
  }
};


const normalizeList = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.rows)) {
    return payload.rows;
  }

  if (Array.isArray(payload?.content)) {
    return payload.content;
  }

  if (Array.isArray(payload?.detailRows)) {
    return payload.detailRows;
  }

  return [];
};

const normalizeSkuStatusRows = (payload, statusPrefix) =>
  normalizeList(payload).map((row, index) => {
    const normalizedRow = {
      ...row,
      id: row.id || row.detailId || row.parsedSku || `${statusPrefix}-${index}`,
      status: row.status || statusPrefix,
    };

    setIfDefined(normalizedRow, "batchId", pickFirstDefined(row.batchId, row.batchID, row.batch_id, row.batchid));
    setIfDefined(normalizedRow, "batchType", pickFirstDefined(row.batchType, row.type, row.batch_type));

    const totalSkus = pickFirstDefined(row.totalSkus, row.totalSKU, row.totalSkuCount, row.totalSkusCount);
    const oldMargin = pickFirstDefined(row.oldMargin, row.oldMarginDollar, row.oldMarginDollars);
    const newMargin = pickFirstDefined(row.newMargin, row.newMarginDollar, row.newMarginDollars);
    const percentChangeMargin = pickFirstDefined(row.percentChangeMargin, row.marginPercentChange);
    const dollarChangeMargin = pickFirstDefined(row.dollarChangeMargin, row.marginDollarChange);
    const onHoldItems = pickFirstDefined(row.onHoldItems, row.onHoldCount);
    const flaggedItems = pickFirstDefined(row.flaggedItems, row.flaggedCount);
    const priorityCount = pickFirstDefined(row.priorityCount, row.priorityCountItems);
    const regularCount = pickFirstDefined(row.regularCount, row.regularCountItems);
    const newCount = pickFirstDefined(row.newCount, row.newCountItems);

    setIfDefined(normalizedRow, "totalSkus", totalSkus !== undefined ? toNumber(totalSkus) : undefined);
    setIfDefined(normalizedRow, "oldMargin", oldMargin !== undefined ? toNumber(oldMargin) : undefined);
    setIfDefined(normalizedRow, "newMargin", newMargin !== undefined ? toNumber(newMargin) : undefined);
    setIfDefined(normalizedRow, "percentChangeMargin", percentChangeMargin !== undefined ? toNumber(percentChangeMargin) : undefined);
    setIfDefined(normalizedRow, "dollarChangeMargin", dollarChangeMargin !== undefined ? toNumber(dollarChangeMargin) : undefined);
    setIfDefined(normalizedRow, "onHoldItems", onHoldItems !== undefined ? toNumber(onHoldItems) : undefined);
    setIfDefined(normalizedRow, "flaggedItems", flaggedItems !== undefined ? toNumber(flaggedItems) : undefined);
    setIfDefined(normalizedRow, "priorityCount", priorityCount !== undefined ? toNumber(priorityCount) : undefined);
    setIfDefined(normalizedRow, "regularCount", regularCount !== undefined ? toNumber(regularCount) : undefined);
    setIfDefined(normalizedRow, "newCount", newCount !== undefined ? toNumber(newCount) : undefined);
    setIfDefined(
      normalizedRow,
      "lastDateToApprove",
      pickFirstDefined(row.lastDateToApprove, row.lastApprovalDate, row.lastDate, row.approvalDate)
    );

    return normalizedRow;
  });

const normalizeEscalatedSkuStatusRows = (payload) =>
  normalizeList(payload).map((row, index) => {
    const normalizedRow = {
      ...row,
      id: row.id || row.detailId || row.parsedSku || row.batchId || row.batchID || `Escalated-${index}`,
    };

    setIfDefined(normalizedRow, "batchId", pickFirstDefined(row.batchId, row.batchID, row.batch_id, row.batchid));

    return normalizedRow;
  });

const buildSkuStatusRequestBody = (row, actionType, updatedBy) => {
  const parsedSku = row?.parsedSku || row?.skuId || row?.id || "";
  const hqLine = row?.hqLine || "";
  const batchId = pickFirstDefined(row?.batchId, row?.batchID, row?.batch_id, row?.batchid) || "";
  const normalizedAction = actionType === "Hold" || actionType === "Block" ? "BLOCKED" : "FLAGGED";
  const comments =
    normalizedAction === "BLOCKED"
      ? "Blocked for pricing review"
      : "Flagged for pricing review";

  return {
    parsedSku,
    hqLine,
    batchId,
    status: normalizedAction,
    updatedBy: updatedBy || "",
    comments,
  };
};

const buildRestoreSkuStatusRequestBody = (row, updatedBy) => ({
  parsedSku: row?.parsedSku || row?.skuId || row?.id || "",
  hqLine: row?.hqLine || "",
  batchId: pickFirstDefined(row?.batchId, row?.batchID, row?.batch_id, row?.batchid) || "",
  status: "ESTIMATED",
  updatedBy: updatedBy || "",
  comments: "Restored to estimated for pricing review",
});

const getSkuStatusEndpoint = (endpointKey, fallbackPath) =>
  `${localDatabaseUrls.baseURL}${localDatabaseUrls.serviceURL[endpointKey] || fallbackPath}`;


const loadSkuStatusRows = async (statusPath, errorMessage, statusPrefix) => {
  const tokenInfo = await getJWTToken();

  if (!tokenInfo) {
    window.location.href = "/";
    return [];
  }

  const apiUrl = `${localDatabaseUrls.baseURL}${statusPath}`;
  const response = await getAPI(apiUrl, tokenInfo);

  if (response.ok) {
    const payload = await response.json().catch(() => []);
    return normalizeSkuStatusRows(payload, statusPrefix);
  }

  const errorData = await response.json().catch(() => ({}));
  AlertManager.error(errorData?.message || errorMessage, { contactSupport: false });
  return [];
};

const loadRawSkuStatusRows = async (statusPath, errorMessage) => {
  const tokenInfo = await getJWTToken();

  if (!tokenInfo) {
    window.location.href = "/";
    return [];
  }

  const apiUrl = `${localDatabaseUrls.baseURL}${statusPath}`;
  const response = await getAPI(apiUrl, tokenInfo);

  if (response.ok) {
    const payload = await response.json().catch(() => []);
    return normalizeEscalatedSkuStatusRows(payload);
  }

  const errorData = await response.json().catch(() => ({}));
  AlertManager.error(errorData?.message || errorMessage, { contactSupport: false });
  return [];
};

export const getBlockedSkuStatusRows = async () =>
  loadSkuStatusRows("api/v1/sku-status/blocked", "Failed to load blocked SKU data.", "Blocked");

export const getFlaggedSkuStatusRows = async () =>
  loadSkuStatusRows("api/v1/sku-status/flagged", "Failed to load flagged SKU data.", "Flagged");

export const getEscalatedSkuStatusRows = async () =>
  loadRawSkuStatusRows("api/v1/sku-status/escalated", "Failed to load escalated SKU data.");

export const updateSkuStatus = async (row, actionType) => {
  const tokenInfo = await getJWTToken();

  if (!tokenInfo) {
    window.location.href = "/";
    return null;
  }

  const apiUrl = getSkuStatusEndpoint("skuStatusUpdate", "api/v1/sku-status/update");
  const requestBody = buildSkuStatusRequestBody(row, actionType, tokenInfo.username || "");
  const body = new Blob([JSON.stringify(requestBody)], { type: "application/json" });

  try {
    const response = await putAPI(apiUrl, tokenInfo, body);

    if (response?.ok) {
      AlertManager.success(`${actionType || "Item"} updated successfully.`, { contactSupport: false });
      return response;
    }
    const errorData = await response?.json?.().catch(() => ({}));
    throw new Error(errorData?.message || "Unable to update SKU status.");
  } catch (error) {
    AlertManager.error(error?.message || "Unable to update SKU status.");
    return null;
  }
};

export const restoreSkuStatus = async (row, actionType) => {
  const tokenInfo = await getJWTToken();

  if (!tokenInfo) {
    window.location.href = "/";
    return null;
  }

  const apiUrl = getSkuStatusEndpoint("skuStatusRestore", "api/v1/sku-status/restore");
  const requestBody = buildRestoreSkuStatusRequestBody(row, tokenInfo.username || "");
  const body = new Blob([JSON.stringify(requestBody)], { type: "application/json" });

  try {
    const response = await putAPI(apiUrl, tokenInfo, body);

    if (response?.ok) {
      AlertManager.success(`${actionType || "Item"} restored successfully.`, { contactSupport: false });
      return response;
    }
    const errorData = await response?.json?.().catch(() => ({}));
    throw new Error(errorData?.message || "Unable to restore SKU status.");
  } catch (error) {
    AlertManager.error(error?.message || "Unable to restore SKU status.");
    return null;
  }
};

const dcSkuStatusApi = {
  getBlockedSkuStatusRows,
  getFlaggedSkuStatusRows,
  getEscalatedSkuStatusRows,
  updateSkuStatus,
  restoreSkuStatus,
};
export default dcSkuStatusApi;
